import { gql } from '@apollo/client';
import {
  AncestorIdData,
  AncestorNameData,
  FolderAncestorsData,
  FolderId,
  FolderIdData,
  FolderNameData,
  maybeSplitFolderContents,
} from 'modules/domains/foldering';
import { GRAPHQL_TYPENAMES } from 'modules/graphql/types';
import { useGraphql } from 'utils/hooks/useGraphql';

import { GraphQLCampaign } from '../domainTypes';
import { GraphQLCampaignSubfolder } from '../types';

/** This hook functions similarly to useCampaignFolders but is used for the modals */

export type FetchFolderQueryResponse = {
  campaignFolder: {
    info: {
      count: number;
      limit: number;
      offset: number;
      page: number;
    };
    id: number;
    name: string;
    content: (GraphQLCampaignSubfolder | GraphQLCampaign)[];
    ancestors: (GraphQLCampaignSubfolder | GraphQLCampaign)[];
    __typename: GRAPHQL_TYPENAMES.CAMPAIGN_FOLDER;
  };
};

export const FetchCampaignFolderQuery = gql`
  query FetchCampaignFolderQuery(
    $content: [ContentFilter!]
    $folderId: Long
    $pagination: Pagination
  ) {
    campaignFolder(content: $content, folderId: $folderId, pagination: $pagination) {
      info {
        count
        offset
        limit
        page
      }
      ancestors {
        id
        name
      }
      content {
        ... on CampaignSubfolder {
          id
          name
        }
        ... on Campaign {
          id
          name
        }
      }
      id
      name
    }
  }
`;

type FolderContents = {
  childFolders: GraphQLCampaignSubfolder[];
  childCampaigns: GraphQLCampaign[];
};

type FolderData = {
  folderData: FolderAncestorsData<AncestorIdData & AncestorNameData> &
    FolderIdData &
    FolderNameData;
};

export type CampaignFolderData = FolderContents & FolderData;

export function useCampaignFolder(queryParams: {
  folderId: FolderId;
  pagination?: { offset: number; limit: number };
}): CampaignFolderData {
  const { data, ...response } = useGraphql(FetchCampaignFolderQuery, {
    variables: { ...queryParams },
    fetchPolicy: 'no-cache',
  });

  const [folders, campaigns] = maybeSplitFolderContents<GraphQLCampaignSubfolder, GraphQLCampaign>(
    GRAPHQL_TYPENAMES.CAMPAIGN,
    data.campaignFolder?.content,
  );
  return {
    ...response,
    childFolders: folders,
    childCampaigns: campaigns,
    folderData: {
      name: data.campaignFolder?.name ?? '',
      id: data.campaignFolder?.id ?? null,
      ancestors: data.campaignFolder?.ancestors ?? [],
    },
  };
}
